import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { cssVar } from '../../../utils/cssVar'
import CodeBlock from '../../atoms/CodeBlock'
import echarts from './config'
import { useChartColors } from './ColorProvider'
import LoadingState from './LoadingState'
import EmptyState from './EmptyState'

const defaultChartConfig = (restProps, themeColors) => ({
  color: (themeColors || []),
  ...(restProps || {}),
  textStyle: {
    fontFamily: cssVar('--font-sans'),
    ...(restProps?.textStyle || {})
  }
})

const ChartContext = createContext({})

export const useChartContext = () => {
  return useContext(ChartContext)
}

function ChartProvider (
  {
    width,
    height,
    debug,
    color,
    theme,
    children,
    loadingTemplate = 'pie',
    loadingTemplateProps,
    emptyTemplate,
    emptyTemplateProps,
    ...restProps
  }) {
  const themeColors = useChartColors(theme)
  const _colors = useMemo(() => {
    if (color?.length) return color
    return themeColors
  }, [color, themeColors])
  const [chartConfig, setChartConfig] = useState(defaultChartConfig(restProps, _colors))
  const [seriesConfig, setSeriesConfig] = useState({})
  const [loadingState, setLoadingState] = useState({})
  const globalLoading = useMemo(() => Object.values(loadingState).some(Boolean), [loadingState])

  const setSeriesLoading = useCallback((key, isLoading) => {
    setLoadingState(p => ({
      ...p,
      [key]: isLoading
    }))
  }, [])

  const chartRef = useRef(null)
  const chartInstanceRef = useRef(null)

  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current)
      window.addEventListener('resize', chartInstanceRef.current.resize)
    }

    let observer = null
    let raf = null
    if (chartRef.current && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (raf != null) return
        raf = requestAnimationFrame(() => {
          raf = null
          if (chartInstanceRef.current) chartInstanceRef.current.resize()
        })
      })
      observer.observe(chartRef.current)
    }

    return () => {
      if (raf != null) {
        cancelAnimationFrame(raf)
        raf = null
      }
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (chartInstanceRef.current) {
        window.removeEventListener('resize', chartInstanceRef.current.resize)
        chartInstanceRef.current.dispose()
        chartInstanceRef.current = null
      }
    }
  }, [])

  const setKeyConfig = useCallback((key, config) => {
    setChartConfig(p => ({
      ...p,
      [key]: config
    }))
  }, [])

  const setSeries = useCallback((key, series) => {
    setSeriesConfig(p => ({
      ...p,
      [key]: series
    }))
  }, [])

  const removeSeries = useCallback((key) => {
    setSeriesConfig(p => {
      const updated = { ...p }
      delete updated[key]
      return updated
    })
  }, [])

  const chartContextValue = useMemo(() => {
    return {
      setSeries,
      removeSeries,
      setKeyConfig,
      setSeriesLoading,
      loadingState
    }
  }, [setSeries, removeSeries, setKeyConfig, setSeriesLoading, loadingState])

  const combinedOptions = useMemo(() => {
    const series = Object.values(seriesConfig)
    if (chartInstanceRef.current && series.length) {
      return {
        ...chartConfig,
        loadingState,
        series
      }
    } else {
      return {
        loadingState
      }
    }
  }, [seriesConfig, chartConfig, loadingState])

  const loadingProps = useMemo(() => {
    return {
      ...(chartConfig ?? {}),
      ...loadingTemplateProps
    }
  }, [loadingTemplateProps, chartConfig])

  const emptyProps = useMemo(() => {
    return {
      ...(chartConfig ?? {}),
      title: 'No Data Available',
      ...(emptyTemplateProps ?? loadingTemplateProps)
    }
  }, [emptyTemplateProps, loadingTemplateProps, chartConfig])

  const setOptions = useCallback(() => {
    if (chartInstanceRef.current && combinedOptions) {
      const chart = chartInstanceRef.current
      chart.setOption(combinedOptions, { replaceMerge: ['series'] })

      return true
    }
  }, [combinedOptions])

  useEffect(() => {
    setOptions()
  }, [setOptions])

  const allOptionsEmpty = useMemo(() => {
    return Object.values(seriesConfig)?.every(s => !s?.data?.length)
  }, [seriesConfig])

  const shouldShowChart = useMemo(() => {
    return !globalLoading && !allOptionsEmpty
  }, [globalLoading, allOptionsEmpty])

  return (
    <ChartContext.Provider value={chartContextValue}>
      <div className='relative' style={{ height, width }}>
        <div
          ref={chartRef}
          className={clsx({
            'fade-in': shouldShowChart,
            'opacity-0': !shouldShowChart
          })}
          style={{ height, width }}
        />
        {children}
        {!globalLoading && allOptionsEmpty && <EmptyState type={emptyTemplate ?? loadingTemplate} {...emptyProps} />}
        {globalLoading && <LoadingState type={loadingTemplate} {...loadingProps} />}
        {debug ? (<CodeBlock code={combinedOptions} />) : null}
      </div>
    </ChartContext.Provider>
  )
}

ChartProvider.propTypes = {
  theme: PropTypes.string,
  color: PropTypes.array,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  debug: PropTypes.bool,
  children: PropTypes.node,
  emptyTemplate: PropTypes.oneOf(['pie', 'axis']),
  emptyTemplateProps: PropTypes.object,
  loadingTemplate: PropTypes.oneOf(['pie', 'axis']),
  loadingTemplateProps: PropTypes.object
}

export default ChartProvider
